/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {Typography} from '@material-ui/core';
import {ClassNameMap} from '@material-ui/core/styles/withStyles';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {PhoneNumber} from '@ion/dls/src/components/phone/PhoneNumber';
import {isPossiblePhoneNumber} from '@ion/dls/src/components/phone/Utils';
import {Entry, UserInfoFields} from './Users';

export interface UserLevelProps {
  handleFieldChange?: Function;
  onPhoneNumberChange?: Function;
  onDelete?: Function;
  classes?: ClassNameMap<string>,
  usersLevel: Entry[];
  item?: Entry;
  minUser?: number;
  index?: number;
};

/**
 * The User Level provides the UI which allows to add new users,
 *  such as their firstname, lastname, email and phone numbers on
 *  click od Add button.
 *
 *    import {UserLevel} from '@ion/applications/src/pages/admin/accounts/UserLevel';
 *
 *    <UserLevel
 *       onChange={onChange} />
 *
 */
export const UserLevel = ({
  onPhoneNumberChange,
  handleFieldChange,
  onDelete,
  usersLevel,
  item,
  index,
  classes,
  minUser = 0
}: UserLevelProps) => {
  const strId = item.id?.toString();
  const user = item.user;

  return (
    <>
      <div className={classes.subHeader}>
        <Typography className={classes.typoStyle} component='h1' variant='h2'>
          {translate('admin-groups:dialog.fields.userLevel', {level: index + 1})}
        </Typography>
        {usersLevel?.length > minUser && (
          <TertiaryButton className={classes.deleteStyle}
            key={`deleteButton_${strId}`}
            id={strId}
            onClick={onDelete(strId)}>
            {translate('common:delete')}
          </TertiaryButton>
        )}
      </div>
      <FormItem>
        <FormTextField required
          fieldLabel={translate('admin-groups:dialog.firstName.label')}
          placeholder={translate('admin-groups:dialog.firstName.placeholder')}
          name={UserInfoFields.FirstName}
          key={`${UserInfoFields.FirstName}_${strId}`}
          id={strId}
          maxLength={64}
          onChange={handleFieldChange}
          hasError={user?.isFirstNameError}
          errorText={translate('common:editors.shared.required')}
          value={user?.firstName} />
      </FormItem>
      <FormItem>
        <FormTextField required
          fieldLabel={translate('admin-groups:dialog.lastName.label')}
          placeholder={translate('admin-groups:dialog.lastName.placeholder')}
          name={UserInfoFields.LastName}
          key={`${UserInfoFields.LastName}_${strId}`}
          id={strId}
          maxLength={64}
          onChange={handleFieldChange}
          hasError={user?.isLastNameError}
          errorText={translate('common:editors.shared.required')}
          value={user?.lastName} />
      </FormItem>
      <FormItem>
        <FormTextField required
          fieldLabel={translate('admin-groups:dialog.email.label')}
          placeholder={translate('admin-groups:dialog.email.placeholder')}
          name={UserInfoFields.EmailAddress}
          key={`${UserInfoFields.EmailAddress}_${strId}`}
          id={strId}
          maxLength={64}
          onChange={handleFieldChange}
          hasError={user?.isEmailError}
          errorText={user?.email === '' ? translate('common:editors.shared.required') : translate('admin-groups:dialog.email.error')}
          value={user?.email} />
      </FormItem>
      <FormItem>
        <PhoneNumber required
          disabled={false}
          formValidation={{
            isEmptyPhoneNumber   : user?.isPhoneError,
            isInvalidPhoneNumber : !isEmpty(user?.phoneNumber) && !isPossiblePhoneNumber(user?.phoneNumber)
          }}
          id={strId}
          onChange={onPhoneNumberChange}
          labelText={translate('admin-groups:dialog.number.label')}
          placeholderText={translate('admin-groups:dialog.number.placeholder')}
          existingPhoneNumber={user?.phoneNumber} />
      </FormItem>
    </>
  );
};
export default UserLevel;
